import { Link, useLoaderData, useNavigate } from "react-router-dom";
import { Organisation } from "../dtos/Organisation";
import { Alert, Button, Card, Col, Form, Modal, Row, Tab, Tabs } from "react-bootstrap";
import React, {
  FormEvent,
  useCallback,
  useEffect,
  useId,
  useState,
} from "react";
import { deleteData, getData, storeData } from "../utils/storage/Storage";
import { StorageKey } from "../utils/storage/Keys";
import { NetworkCalls } from "../NetworkCalls";
import { UserCatchReport } from "../dtos/UserCatchReport";
import { CatchReportUser } from "../dtos/CatchReportUser";
import CatchesTable from "./CatchesTable";
import NewCatchInputForm from "./NewCatchInputForm";
import { UserCatchReportAddFormData } from "../dtos/UserCatchReportAddFormData";
import { CatchYear } from "../dtos/CatchYear";
import TimestampFormat from "../utils/TimestampFormat";
import { ReportCatchYearDTO } from "../dtos/ReportCatchYearDTO";

export default function CatchReportMemberPage() {
  const organisation = useLoaderData() as Organisation;
  const navigate = useNavigate();

  const [catches, setCatches] = useState<UserCatchReport[]>([]);
  const [user, setUser] = useState<CatchReportUser>({
    lastName: "",
    memberNumber: "",
    saveInputs: false,
  });

  const [validated, setValidated] = useState<boolean>(false);
  const [catchReportAvailable, setCatchReportsAvailable] =
    useState<boolean>(true);

  const [catchYear, setCatchYear] = useState<CatchYear | undefined>();
  const [closedCatchYear, setClosedCatchYear] = useState<CatchYear | undefined>();
  const [reportedYears, setReportedYears] = useState<ReportCatchYearDTO[]>([]);
  const [selectedCatchYearFilter, setSelectedCatchYearFilter] =
    useState<string>("all");
  // const [reportedCatchYears, setReportedCatchYears] = useState<ReportCatchYearDTO[] | undefined>();

  const [catchYearModal, setCatchYearModal] = useState<boolean>(false);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertVariant, setAlertVariant] = useState<string | undefined>();
  const [alertText, setAlertText] = useState<string>("");
  const [alertHeaderText, setAlertHeaderText] = useState<string>("");
  const [reloadData, setReloadData] = useState<boolean>(false);

  const formLabelCols = 3;
  const formInputCols = 12 - formLabelCols;

  const updateCatches = useCallback(() => {
    if (!user.memberNumber || !user.lastName) return Promise.resolve();

    return NetworkCalls.getUserCatchReports({
      organisationToken: organisation.organisationToken,
      memberNumber: user.memberNumber,
      lastName: user.lastName,
    })
      .then((result) => {
      result.sort((a, b) => b.catchTime - a.catchTime);
      setCatches(result);
      })
      .catch((error) => {
        if (error?.response?.status === 404) {
          deleteData(StorageKey.CATCH_REPORT_USER);
          navigate(`/catch-report/${organisation.organisationToken}`, {
            replace: true
          });
          return;
        }
        throw error;
      });
  }, [user.lastName, user.memberNumber, organisation.organisationToken, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      const result = await NetworkCalls.reportCatchYearAvailable({
        organisationId: organisation.id,
      });

      const myReportedYears = await NetworkCalls.listMyReportedCatchYears({
        organisationId: organisation.id,
        memberNumber: user.memberNumber,
        lastName: user.lastName,
      });
      setReportedYears(myReportedYears);

      const catchYearReported = myReportedYears.find(
        (elem) => elem.catchYearId === result.id
      );

      const currentDate = new Date();
      const isInReportRange =
        result.startReport < currentDate.getTime() &&
        result.endReport >= currentDate.getTime();
      const isInCatchYearRange =
        result.startCatchYear < currentDate.getTime() &&
        result.endCatchYear >= currentDate.getTime();
      setCatchYear(undefined);
      setCatchReportsAvailable(true);
      setClosedCatchYear(undefined);
      if (catchYearReported !== undefined) {
        setClosedCatchYear(result);
        if (isInCatchYearRange) {
          setShowAlert(true);
          setAlertVariant("info");
          setAlertHeaderText("Information");
          setAlertText(
            "Du kannst aktuell keine Fänge eintragen, da du das aktuelle Fangjahr bereits abgeschlossen hast"
          );
          setCatchReportsAvailable(false);
        }
      } else if (isInReportRange) {
        setCatchYear(result);
      }

      // setReportedCatchYears(myReportedYears);
    };
    if (reloadData) {
      setReloadData(false);
      updateCatches()
        .then(() => {
          fetchData().then();
        })
        .catch(console.error);
    }
  }, [
    updateCatches,
    reloadData,
    organisation.id,
    user.lastName,
    user.memberNumber,
  ]);

  useEffect(() => {
    const dataStringUser = getData(StorageKey.CATCH_REPORT_USER) || "{}";
    const users = JSON.parse(dataStringUser);
    if (users[organisation.organisationToken]) {
      setUser(users[organisation.organisationToken]);
      setReloadData(true);
    }
  }, [organisation.organisationToken]);

  const renderH1 = () => {
    return (
      <>
        Fangrückmeldung - {organisation.organisationName} - Vereinsmitglied:{" "}
        {user.lastName} ({user.memberNumber})
      </>
    );
  };

  const clearUserData = () => {
    if (!user.saveInputs) {
      const dataStringUser = getData(StorageKey.CATCH_REPORT_USER) || "{}";
      const users = JSON.parse(dataStringUser);
      if (
        users[organisation.organisationToken] &&
        !users[organisation.organisationToken].saveInputs
      ) {
        users[organisation.organisationToken] = undefined;
        storeData(StorageKey.CATCH_REPORT_USER, JSON.stringify(users));
      }
    }
  };

  const renderCardFooter = () => {
    return (
      <Card.Footer>
        <Link
          to={`/catch-report/${organisation.organisationToken}`}
          className="btn btn-secondary"
          onClick={clearUserData}
        >
          Fertig
        </Link>
      </Card.Footer>
    );
  };

  const onSubmit = (
    event: FormEvent<HTMLFormElement>,
    formData: UserCatchReportAddFormData
  ): Promise<void> => {
    const form = event.currentTarget;

    event.stopPropagation();
    event.preventDefault();

    let checkValidity = form.checkValidity();
    setValidated(true);

    if (!checkValidity) {
      return Promise.reject("Es fehlen Eingaben");
    }

    formData.memberId = user.memberNumber;
    formData.lastName = user.lastName;

    if (
      closedCatchYear &&
      formData.catchTime >= closedCatchYear.startCatchYear &&
      formData.catchTime <= closedCatchYear.endCatchYear
    ) {
      setShowAlert(true);
      setAlertVariant("danger");
      setAlertHeaderText("Nicht erlaubt");
      setAlertText(
        "Du kannst keine Fänge im abgeschlossenen Fangjahr eintragen."
      );
      return Promise.reject("Fangjahr bereits abgeschlossen");
    }

    return NetworkCalls.addUserCatchResult(formData).then(async (_) => {
      setValidated(false);
      await updateCatches();
    });
  };

  const showContent = () => {
    const catchYears = Array.from(
      new Set(catches.map((c) => new Date(c.catchTime).getFullYear()))
    ).sort((a, b) => b - a);
    const filteredCatches =
      selectedCatchYearFilter === "all"
        ? catches
        : catches.filter(
            (c) =>
              new Date(c.catchTime).getFullYear() ===
              Number(selectedCatchYearFilter)
          );
    return (
      <>
        {CloseCatchYearModal()}
        <div className="page page-center">
          <div className="container py-4">
            <div className="m-5 col row justify-content-center">
              <Alert
                show={showAlert}
                variant={alertVariant}
                onClose={() => setShowAlert(false)}
                dismissible
              >
                <Alert.Heading>{alertHeaderText}</Alert.Heading>
                <p>{alertText}</p>
              </Alert>
              <Card>
                <Card.Header>
                  <div className="d-flex">
                    <h1>{renderH1()}</h1>
                    <Button
                      hidden={catchYear === undefined}
                      className="btn btn-azure"
                      style={{ marginLeft: "1rem" }}
                      onClick={() => setCatchYearModal(true)}
                    >
                      Fangjahr abschließen
                    </Button>
                  </div>
                </Card.Header>
                <Card.Body>
                  <Tabs defaultActiveKey="catches" className="mb-3">
                    <Tab eventKey="catches" title="Fänge">
                      <Row>
                        <Col md={6}>
                          <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between gap-2 mb-3">
                            <h2 className="mb-0">Eingetragene Fänge</h2>
                            <Form.Select
                              size="sm"
                              style={{maxWidth: 180}}
                              value={selectedCatchYearFilter}
                              onChange={(e) => setSelectedCatchYearFilter(e.target.value)}
                            >
                              <option value="all">Alle Jahre</option>
                              {catchYears.map((year) => (
                                <option key={year} value={year}>
                                  {year}
                                </option>
                              ))}
                            </Form.Select>
                          </div>
                          <div className="text-muted mb-2">
                            {filteredCatches.length} Einträge
                          </div>
                          <CatchesTable catches={filteredCatches} showTitle={false} />
                        </Col>
                        <hr className="d-md-none" />
                        <Col md={6} hidden={!catchReportAvailable}>
                          <h2>Neuen Fang eintragen</h2>
                          <NewCatchInputForm
                            onSubmit={onSubmit}
                            validated={validated}
                            formLabelCols={formLabelCols}
                            formInputCols={formInputCols}
                            organisation={organisation}
                          />
                        </Col>
                      </Row>
                    </Tab>
                    <Tab eventKey="catchyears" title="Fangjahre">
                      <div className="text-muted mb-3">
                        Hier siehst du deine abgeschlossenen Fangjahre.
                      </div>
                      {reportedYears.length === 0 ? (
                        <div className="text-muted">
                          Keine abgeschlossenen Fangjahre gefunden.
                        </div>
                      ) : (
                        <div className="list-group">
                          {reportedYears.map((year) => (
                            <div
                              key={year.id}
                              className="list-group-item d-flex justify-content-between align-items-center"
                            >
                              <div>
                                <div className="fw-semibold">
                                  {year.reportedCatchYearName}
                                </div>
                                <div className="text-muted small">
                                  {TimestampFormat.toDateOnlyReadableFormat(
                                    year.reportedDate
                                  )}
                                </div>
                              </div>
                              <span className="text-muted">
                                {year.number}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </Tab>
                  </Tabs>
                </Card.Body>
                {renderCardFooter()}
              </Card>
            </div>
          </div>
        </div>
      </>
    );
  };

  const handleClose = () => {
    setCatchYearModal(false);
  };

  const CloseCatchYearModal = () => {
    const modalId = useId();
    return (
      <Modal
        show={catchYearModal && catchYear !== undefined}
        onHide={handleClose}
        id={modalId}
      >
        <Modal.Header closeButton>
          <Modal.Title>Fangjahr {catchYear?.name} abschließen</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Möchtest du wirklich das Fangjahr {catchYear?.name} abschließen?
          Danach kannst du für den Zeitraum{" "}
          {catchYear !== undefined && catchYear.startCatchYear !== undefined
            ? TimestampFormat.toDateOnlyReadableFormat(
              catchYear.startCatchYear
              )
            : ""}{" "}
          -{" "}
          {catchYear !== undefined && catchYear.endCatchYear !== undefined
            ? TimestampFormat.toDateOnlyReadableFormat(catchYear.endCatchYear)
            : ""}{" "}
          keine Fänge mehr eintragen
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={() => {
              NetworkCalls.reportCatchYear({
                organisationId: organisation.id,
                memberNumber: user.memberNumber,
              })
                .then(() => {
                  setShowAlert(true);
                  setAlertVariant("success");
                  setAlertHeaderText("Erfolgreich");
                  setAlertText(
                    "Das Fangjahr wurde erfolgreich abgeschlossen. Du musst nichts weiter tun"
                  );
                  setReloadData(true);
                })
                .catch((error) => {
                  setShowAlert(true);
                  setAlertVariant("danger");
                  setAlertHeaderText("Fehler");
                  setAlertText(
                    "Es ist ein Fehler aufgetreten. Verscuhe es erneut oder wende dich an info@fangkarte.de"
                  );
                })
                .finally(() => setCatchYearModal(false));
            }}
          >
            Fangjahr abschließen
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  if (!user) {
    return (
      <>
        <Alert variant="danger">Kein Benutzer angegeben</Alert>
        <Link
          to={`/catch-report/${organisation.organisationToken}`}
          className="btn btn-secondary"
        >
          Zurück
        </Link>
      </>
    );
  }
  return <>{showContent()}</>;
}
